import { useParams } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import { FilledDatasetValue } from '../../lib/api';
import { useIsSupplier } from '../../hooks/usePortalMode';
import { useAuth } from '../../contexts/AuthContext';
import { QuestionRenderer } from './QuestionRenderer';
import QuestionResubmissionWrapper, { getQuestionLockState } from './QuestionResubmissionWrapper';
import ScorePreviewCard from './ScorePreviewCard';
import { AutoSaveIndicator } from '../../components/common/AutoSaveIndicator';
import { unsavedChangesPrompt } from '../../hooks/useUnsavedChangesGuard';
import { useFillQuestionnaire } from './useFillQuestionnaire';
import { statusColors } from './questionnaireTypes';


export default function FillQuestionnaire() {
  // CR-8252: an approved supplier has no My Onboarding page; its questionnaire
  // (a renewal, or one sent later) goes straight to the customer.
  const { user } = useAuth();
  const supplierApproved = user?.supplier_is_approved !== false;
  const { id } = useParams<{ id: string }>();
  const isSupplier = useIsSupplier();

  // CR-6165: all state and behaviour moved into useFillQuestionnaire. The page
  // was 722 lines — over the 500-line limit — and adding auto-save inline would
  // have pushed it further. This also makes it mirror useQualificationDetail,
  // so the two questionnaire surfaces stay the same shape.
  const {
    navigate,
    loading,
    saving,
    submitting,
    filledDataset,
    answers,
    showSubmitDialog,
    setShowSubmitDialog,
    expandedSections,
    setExpandedSections,
    uploadingFiles,
    uploadedFiles,
    carriedForwardIds,
    carriedFromLabel,
    scorePreview,
    loadingScore,
    autoSaveStatus,
    lastSavedAt,
    unsavedCount,
    isReadOnly,
    resubmissionMode,
    shouldShowSection,
    shouldShowQuestion,
    getMissingRequiredQuestions,
    getAnsweredCount,
    getTotalQuestions,
    handleAnswerChange,
    handleFileUpload,
    handleFileRemove,
    handleSave,
    handleSubmit,
  } = useFillQuestionnaire(id);

  // CR-6165: auto-save closes most of the gap, but the seconds between a
  // keystroke and the debounce landing are real work. Ask before discarding it.
  const handleBack = () => {
    if (unsavedCount > 0 && !window.confirm(unsavedChangesPrompt(unsavedCount))) return;
    navigate('/onboarding/my-progress');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!filledDataset) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Questionnaire not found</h3>
            <Button variant="outline" onClick={() => navigate('/onboarding/my-progress')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Onboarding
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalQuestions = getTotalQuestions();
  const answeredCount = getAnsweredCount();
  const completionPercentage = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  // CR-5362 Phase 3: while the questionnaire is back with the supplier
  // (more_info_required), expose the customer's per-question decisions
  // so already-approved answers lock and rejected ones are flagged for
  // revision. Outside resubmission mode this lookup is a no-op.
  const valuesByQuestion: Record<number, FilledDatasetValue> = {};
  (filledDataset.values ?? []).forEach((v) => {
    valuesByQuestion[v.question] = v;
  });
  const rejectedQuestionCount = resubmissionMode
    ? Object.values(valuesByQuestion).filter((v) => v.question_status === 'rejected').length
    : 0;
  const lockedQuestionCount = resubmissionMode
    ? Object.values(valuesByQuestion).filter(
        (v) => v.question_status === 'approved' || v.question_status === 'approved_with_concerns',
      ).length
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack} aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{filledDataset.dataset_name || 'Questionnaire'}</h1>
            <p className="text-muted-foreground">
              {filledDataset.dataset_detail?.description || 'Complete the questionnaire below'}
            </p>
          </div>
        </div>
        <Badge className={statusColors[filledDataset.status] || statusColors['not_started']}>
          {filledDataset.status?.replace(/_/g, ' ')}
        </Badge>
      </div>

      {/* Progress Card */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progress</span>
            <span className="text-sm text-muted-foreground">
              {answeredCount} of {totalQuestions} questions answered
            </span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">{completionPercentage}% complete</p>
        </CardContent>
      </Card>

      {scorePreview && !isSupplier && (
        <ScorePreviewCard scorePreview={scorePreview} loading={loadingScore} />
      )}

      {/* More Info Required Banner */}
      {filledDataset.status === 'more_info_required' && (
        <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-600">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">More information required</span>
            </div>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              The reviewer has requested additional information. Please review the feedback below and update your answers.
            </p>
            {/* CR-5362 Phase 3: surface per-question rollup so the
                supplier knows exactly how many answers need revision
                and how many are already approved (locked). */}
            {(rejectedQuestionCount > 0 || lockedQuestionCount > 0) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {rejectedQuestionCount > 0 && (
                  <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
                    {rejectedQuestionCount} {rejectedQuestionCount === 1 ? 'question' : 'questions'} need revision
                  </Badge>
                )}
                {lockedQuestionCount > 0 && (
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                    {lockedQuestionCount} previously approved · locked
                  </Badge>
                )}
              </div>
            )}
            {filledDataset.more_info_feedback && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-amber-300 dark:border-amber-600">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">Reviewer's Feedback:</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{filledDataset.more_info_feedback}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rejected Banner */}
      {filledDataset.status === 'rejected' && (
        <Card className="border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-600">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">Questionnaire Rejected</span>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              This questionnaire has been rejected by the reviewer.
            </p>
            {filledDataset.rejection_reason && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-red-300 dark:border-red-600">
                <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">Reason for Rejection:</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{filledDataset.rejection_reason}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sections */}
      <Accordion
        type="multiple"
        value={expandedSections}
        onValueChange={setExpandedSections}
        className="space-y-4"
      >
        {filledDataset.dataset_detail?.sections
          ?.filter(shouldShowSection)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((section) => (
            <AccordionItem
              key={section.id}
              value={`section-${section.id}`}
              className="border rounded-lg"
            >
              <AccordionTrigger className="px-4 hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div className="text-left">
                    <h3 className="font-medium">{section.title}</h3>
                    {section.description && (
                      <p className="text-sm text-muted-foreground">{section.description}</p>
                    )}
                  </div>
                  <Badge variant="outline" className="ml-2">
                    {section.questions?.filter(shouldShowQuestion).length || 0} questions
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4">
                  {section.questions
                    ?.filter(shouldShowQuestion)
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((question) => {
                      const reviewedValue = valuesByQuestion[question.id];
                      const lockState = getQuestionLockState(reviewedValue, resubmissionMode);
                      return (
                        <QuestionResubmissionWrapper
                          key={question.id}
                          reviewedValue={reviewedValue}
                          resubmissionMode={resubmissionMode}
                        >
                          <QuestionRenderer
                            question={question}
                            value={answers[question.id]}
                            isReadOnly={isReadOnly || lockState.locked}
                            uploadedFile={uploadedFiles[question.id]}
                            isUploading={uploadingFiles[question.id]}
                            isCarriedForward={carriedForwardIds.has(question.id)}
                            carriedFromLabel={carriedFromLabel}
                            onAnswerChange={handleAnswerChange}
                            onFileUpload={handleFileUpload}
                            onFileRemove={handleFileRemove}
                          />
                        </QuestionResubmissionWrapper>
                      );
                    })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
      </Accordion>

      {/* No sections message */}
      {(!filledDataset.dataset_detail?.sections ||
        filledDataset.dataset_detail.sections.filter(shouldShowSection).length === 0) && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No questions available</h3>
            <p className="text-muted-foreground">
              This questionnaire doesn't have any questions yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {!isReadOnly && (filledDataset.dataset_detail?.sections?.length ?? 0) > 0 && (
        <Card>
          <CardFooter className="flex justify-between items-center gap-3 py-4">
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Progress
                  </>
                )}
              </Button>
              <AutoSaveIndicator
                status={autoSaveStatus}
                lastSavedAt={lastSavedAt}
                hasUnsavedChanges={unsavedCount > 0}
                saveButtonLabel="Save Progress"
              />
            </div>
            <Button onClick={() => setShowSubmitDialog(true)} disabled={saving || submitting}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Complete
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Read-only notice */}
      {/* CR-4762: When status === 'submitted' the old template produced
          "This questionnaire has been submitted and is submitted." — redundant
          and confusing. Suppress the trailing clause in that case and fall
          back to the plain confirmation; other statuses (approved,
          under_review, more_info_required) still surface the state. */}
      {isReadOnly && (
        <Card className="bg-muted">
          <CardContent className="py-4 text-center">
            <CheckCircle2 className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">
              {filledDataset.status && filledDataset.status !== 'submitted'
                ? `This questionnaire has been submitted and is ${filledDataset.status.replace(/_/g, ' ')}.`
                : 'This questionnaire has been submitted.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Change log — who changed what on this assessment */}
      {filledDataset.activity_log && filledDataset.activity_log.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Change log</CardTitle>
            <CardDescription>Chronological record of changes made to this assessment.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {filledDataset.activity_log.slice(0, 20).map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 text-sm border-l-2 border-muted pl-3 py-1">
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{entry.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {entry.performed_by || 'Unknown'}
                    {entry.performed_at ? ` · ${new Date(entry.performed_at).toLocaleString()}` : ''}
                  </p>
                </div>
              </div>
            ))}
            {filledDataset.activity_log.length > 20 && (
              <p className="text-xs text-muted-foreground pt-1">
                Showing the most recent 20 of {filledDataset.activity_log.length} entries.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark questionnaire as complete?</AlertDialogTitle>
            <AlertDialogDescription>
              {supplierApproved
                ? 'You are about to mark this questionnaire as complete. It then goes to your customer for review.'
                : 'You are about to mark this questionnaire as complete. You can submit it for approval later from the My Onboarding page.'}
              {getMissingRequiredQuestions().length > 0 ? (
                <span className="block mt-2 text-amber-600">
                  Warning: You have {getMissingRequiredQuestions().length} required questions unanswered.
                </span>
              ) : (
                <span className="block mt-2 text-green-600">
                  All required questions have been answered.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Complete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
